import { ExternalLink, Server } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { store, useStore } from "@/lib/store";
import type { PlatformProject } from "@/lib/platformTypes";
import { cn } from "@/lib/utils";
import { ConnectPlatformCard, EmptyState, ViewSection, relativeTime } from "@/views/shared";

export function serverStatusBadge(status: string | null | undefined) {
  const failed = status === "error" || status === "failed";
  return (
    <Badge
      variant="outline"
      className={cn(
        "shrink-0 font-mono text-[10px] uppercase tracking-wider",
        failed
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : "border-primary/40 bg-primary/10 text-primary",
      )}
    >
      {status ?? "unknown"}
    </Badge>
  );
}

function ServerRow({ server }: { server: PlatformProject }) {
  return (
    <button
      type="button"
      onClick={() => store.navigate({ name: "server", id: server.id })}
      className="glass-border flex w-full flex-col gap-1 rounded-xl p-3 text-left transition-colors hover:bg-secondary/40"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <Server className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">{server.name}</span>
        </span>
        {serverStatusBadge(server.status)}
      </div>
      <div className="flex items-center justify-between gap-2">
        {server.url ? (
          <a
            href={server.url}
            target="_blank"
            rel="noreferrer"
            // Opening the endpoint must not also navigate into the detail view.
            onClick={(e) => e.stopPropagation()}
            className="inline-flex min-w-0 items-center gap-1 font-mono text-xs text-primary hover:underline"
          >
            <span className="truncate">{server.url.replace(/^https?:\/\//, "")}</span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">no endpoint URL</span>
        )}
        {server.lastDeployAt && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {relativeTime(server.lastDeployAt)}
          </span>
        )}
      </div>
    </button>
  );
}

export function ServersView() {
  const state = useStore();
  const { available, mcpServers } = state.platform;

  if (!available) return <ConnectPlatformCard />;

  if (!mcpServers.loaded) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-16 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4">
      {mcpServers.error && <p className="mb-3 text-sm text-destructive">{mcpServers.error}</p>}

      <ViewSection title="Hosted MCP servers">
        {mcpServers.items.length === 0 ? (
          <EmptyState message="No hosted MCP servers yet. Ask Claude to deploy one with the deploy_mcp_server tool and it will show up here." />
        ) : (
          <div className="flex flex-col gap-2">
            {mcpServers.items.map((s) => (
              <ServerRow key={s.id} server={s} />
            ))}
          </div>
        )}
      </ViewSection>
    </div>
  );
}
